import { useState, useRef, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────

const NOTES_SHARP = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const NOTES_FLAT  = ["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"];

const CIRCLE = [
  { key:"C",  major:"C",  minor:"Am",  sharps:0  },
  { key:"G",  major:"G",  minor:"Em",  sharps:1  },
  { key:"D",  major:"D",  minor:"Bm",  sharps:2  },
  { key:"A",  major:"A",  minor:"F#m", sharps:3  },
  { key:"E",  major:"E",  minor:"C#m", sharps:4  },
  { key:"B",  major:"B",  minor:"G#m", sharps:5  },
  { key:"F#", major:"F#", minor:"D#m", sharps:6  },
  { key:"Db", major:"Db", minor:"Bbm", sharps:-5 },
  { key:"Ab", major:"Ab", minor:"Fm",  sharps:-4 },
  { key:"Eb", major:"Eb", minor:"Cm",  sharps:-3 },
  { key:"Bb", major:"Bb", minor:"Gm",  sharps:-2 },
  { key:"F",  major:"F",  minor:"Dm",  sharps:-1 },
];

const DEG       = ["I","II","III","IV","V","VI","VII"];
const MAJ_TYPES = ["maj","min","min","maj","maj","min","dim"];
const MIN_TYPES = ["min","dim","maj","min","min","maj","maj"];
const FN_MAJ    = ["Tonika","Supertonica","Mediante","Subdominante","Dominante","Submediane","Leitton"];
const FN_MIN    = ["Tonika","Supertonica","Mediante","Subdominante","Dominante","Submediane","Subtonica"];

const FC = {
  Tonika:       { bg:"#e8f5e9", bd:"#81c784", tx:"#2e7d32" },
  Subdominante: { bg:"#e3f2fd", bd:"#64b5f6", tx:"#1565c0" },
  Dominante:    { bg:"#fff8e1", bd:"#ffd54f", tx:"#e65100" },
  Supertonica:  { bg:"#f1f8e9", bd:"#aed581", tx:"#558b2f" },
  Mediante:     { bg:"#ede7f6", bd:"#ba68c8", tx:"#6a1b9a" },
  Submediane:   { bg:"#fce4ec", bd:"#f06292", tx:"#880e4f" },
  Leitton:      { bg:"#fbe9e7", bd:"#ff8a65", tx:"#bf360c" },
  Subtonica:    { bg:"#fbe9e7", bd:"#ff8a65", tx:"#bf360c" },
};

// ─── Diatonic connection rules ────────────────────────────────────────────────
// [fromIdx, toIdx, strength: "strong" | "medium" | "deceptive" | "weak"]

const MAJ_CONN = [
  [4,0,"strong"],    // V→I   authentic cadence
  [6,0,"strong"],    // VII→I leading tone
  [3,0,"strong"],    // IV→I  plagal cadence
  [1,4,"strong"],    // II→V  predominant
  [3,4,"medium"],    // IV→V
  [0,4,"medium"],    // I→V
  [0,3,"medium"],    // I→IV
  [0,5,"medium"],    // I→VI
  [5,1,"medium"],    // VI→II
  [5,3,"medium"],    // VI→IV
  [2,5,"medium"],    // III→VI
  [4,5,"deceptive"], // V→VI  deceptive cadence
  [0,1,"weak"],      // I→II
  [2,3,"weak"],      // III→IV
];

const MIN_CONN = [
  [4,0,"strong"],    // V→i   (harmonic minor)
  [3,0,"strong"],    // iv→i  plagal
  [6,0,"medium"],    // VII→i subtonic
  [1,4,"medium"],    // II°→V
  [3,4,"medium"],    // iv→V
  [0,3,"medium"],    // i→iv
  [0,4,"medium"],    // i→V
  [0,6,"medium"],    // i→VII natural minor feel
  [5,1,"medium"],    // VI→II°
  [5,3,"medium"],    // VI→iv
  [2,5,"medium"],    // III→VI
  [6,2,"medium"],    // VII→III rock/modal
  [4,5,"deceptive"], // V→VI
  [5,6,"weak"],      // VI→VII
];

// ─── Music helpers ────────────────────────────────────────────────────────────

function pickNotes(sharps) { return sharps >= 0 ? NOTES_SHARP : NOTES_FLAT; }
function noteToIdx(n) { const a = NOTES_SHARP.indexOf(n); return a >= 0 ? a : NOTES_FLAT.indexOf(n); }
function enharmonicEq(a, b) { return noteToIdx(a) === noteToIdx(b); }

function buildScale(root, isMinor, sharps) {
  const notes = pickNotes(sharps);
  let ri = notes.indexOf(root);
  if (ri === -1) {
    const alt = sharps >= 0 ? NOTES_FLAT : NOTES_SHARP;
    ri = alt.indexOf(root); if (ri === -1) return [];
    return scaleFrom(ri, isMinor, alt);
  }
  return scaleFrom(ri, isMinor, notes);
}
function scaleFrom(ri, isMinor, notes) {
  const steps = isMinor ? [2,1,2,2,1,2,2] : [2,2,1,2,2,2,1];
  const s = [notes[ri]]; let cur = ri;
  for (const st of steps.slice(0,6)) { cur = (cur+st)%12; s.push(notes[cur]); }
  return s;
}
function noteUp(n, semis, sharps) {
  const arr = pickNotes(sharps);
  let i = arr.indexOf(n);
  if (i === -1) { const alt = sharps>=0?NOTES_FLAT:NOTES_SHARP; i=alt.indexOf(n); return alt[(i+semis+120)%12]; }
  return arr[(i+semis+120)%12];
}
function cLabel(root, type) {
  if (type === "maj") return root;
  if (type === "min") return root + "m";
  return root + "°";
}

// Find the parallel key entry (same root, opposite mode)
function findParallelEntry(root, toMinor) {
  if (toMinor) {
    return CIRCLE.find(c => {
      const mr = c.minor.replace("m","");
      return mr === root || noteToIdx(mr) === noteToIdx(root);
    });
  }
  return CIRCLE.find(c => c.major === root || noteToIdx(c.major) === noteToIdx(root));
}

// Borrowed chords from the parallel mode
function getBorrowedChords(sel, isMinor) {
  const root = isMinor ? sel.minor.replace("m","") : sel.major;
  const parIsMinor = !isMinor;
  const parEntry = isMinor ? findParallelEntry(root, false) : findParallelEntry(root, true);
  if (!parEntry) return [];

  const parScale = buildScale(root, parIsMinor, parEntry.sharps);
  const parTypes = parIsMinor ? MIN_TYPES : MAJ_TYPES;
  const parFns   = parIsMinor ? FN_MIN    : FN_MAJ;

  const diatonic  = buildScale(root, isMinor, sel.sharps);
  const diaTypes  = isMinor ? MIN_TYPES : MAJ_TYPES;

  // Major borrows: bVII(6), bIII(2), bVI(5), iv(3)
  // Minor borrows: Picardy I(0), IV major(3), V major(4)
  const indices = !isMinor ? [6, 2, 5, 3] : [0, 3, 4];
  const results = [];

  for (const i of indices) {
    const r = parScale[i], t = parTypes[i];
    const alreadyExists = diatonic.some((dn,di) => enharmonicEq(dn,r) && diaTypes[di]===t);
    if (alreadyExists) continue;
    results.push({
      root: r, type: t, label: cLabel(r,t),
      fn: parFns[i],
      degree: !isMinor ? "b"+DEG[i] : DEG[i]+"✶",
      desc: "Entlehnt aus " + root + (parIsMinor?"-Moll":"-Dur"),
    });
  }
  return results.slice(0, 3);
}

// Secondary dominant: major chord a 5th above the target chord's root
function getSecDom(chordRoot, targetLabel, sharps) {
  const domRoot = noteUp(chordRoot, 7, sharps);
  return {
    root: domRoot, label: domRoot+"7",
    degree: "V/"+targetLabel,
    desc: "Sekundärdominante von " + targetLabel,
  };
}

// Find other keys where this chord appears (pivot modulations)
function findPivots(chordRoot, chordType, currentSel, currentMode) {
  const pivots = [];
  for (const entry of CIRCLE) {
    for (const mode of ["major","minor"]) {
      if (entry.key===currentSel.key && mode===currentMode) continue;
      const isMin = mode==="minor";
      const root  = isMin ? entry.minor.replace("m","") : entry.major;
      const scale = buildScale(root, isMin, entry.sharps);
      const types = isMin ? MIN_TYPES : MAJ_TYPES;
      for (let i=0; i<scale.length; i++) {
        if (enharmonicEq(scale[i], chordRoot) && types[i]===chordType) {
          pivots.push({
            sel:entry, mode, degree:DEG[i],
            fn:(isMin?FN_MIN:FN_MAJ)[i],
            keyLabel: isMin?entry.minor:entry.major,
            dist: Math.abs(entry.sharps-currentSel.sharps),
          });
        }
      }
    }
  }
  return pivots.sort((a,b)=>a.dist-b.dist).slice(0, 5);
}

// ─── SVG path helper ──────────────────────────────────────────────────────────

const NR = 26; // node radius in px

function arrowPath(from, to, CX, CY, flip) {
  const dx = to.x-from.x, dy = to.y-from.y;
  const len = Math.sqrt(dx*dx+dy*dy) || 1;
  const ux = dx/len, uy = dy/len;
  // Offset by node radius so arrows start/end at node edge
  const sx = from.x + ux*NR,     sy = from.y + uy*NR;
  const ex = to.x   - ux*(NR+6), ey = to.y   - uy*(NR+6);
  // Curve outward from circle center (flip for bidirectional pairs)
  const mx = (sx+ex)/2, my = (sy+ey)/2;
  const ocx = mx-CX, ocy = my-CY;
  const ol  = Math.sqrt(ocx*ocx+ocy*ocy) || 1;
  const sign = flip ? -1 : 1;
  const cpx = mx + sign*(ocx/ol)*26;
  const cpy = my + sign*(ocy/ol)*26;
  return `M${sx.toFixed(1)},${sy.toFixed(1)} Q${cpx.toFixed(1)},${cpy.toFixed(1)} ${ex.toFixed(1)},${ey.toFixed(1)}`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ChordNetwork({ sel, mode, onModulate }) {
  const containerRef = useRef(null);
  const [W, setW]       = useState(360);
  const [selIdx, setSelIdx] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    obs.observe(containerRef.current);
    setW(Math.floor(containerRef.current.offsetWidth) || 360);
    return () => obs.disconnect();
  }, []);

  // Reset when key or mode changes
  useEffect(() => { setSelIdx(null); }, [sel.key, mode]);

  const isMinor   = mode === "minor";
  const root      = isMinor ? sel.minor.replace("m","") : sel.major;
  const scale     = buildScale(root, isMinor, sel.sharps);
  const types     = isMinor ? MIN_TYPES : MAJ_TYPES;
  const fns       = isMinor ? FN_MIN    : FN_MAJ;
  const chords    = scale.map((note,i) => ({
    root: note, type: types[i], label: cLabel(note,types[i]), degree: DEG[i], fn: fns[i],
  }));
  const connections = isMinor ? MIN_CONN : MAJ_CONN;

  const hasSel = selIdx !== null;
  const H  = 400;
  const CX = W / 2;
  const CY = 195;
  const R  = Math.min(W * 0.31, 112);

  // 7 diatonic nodes arranged in a ring
  const nodes = chords.map((c,i) => {
    const angle = (i * 2*Math.PI/7) - Math.PI/2;
    return { ...c, x: CX + R*Math.cos(angle), y: CY + R*Math.sin(angle), angle };
  });

  // Secondary dominant satellite (not for tonic I or diminished)
  let satellite = null;
  if (hasSel && selIdx !== 0 && chords[selIdx].type !== "dim") {
    const chord   = chords[selIdx];
    const node    = nodes[selIdx];
    const sd      = getSecDom(chord.root, chord.label, sel.sharps);
    const satDist = R * 0.65;
    satellite = {
      ...sd,
      x: Math.max(28, Math.min(W-28, node.x + satDist*Math.cos(node.angle))),
      y: Math.max(28, Math.min(H-52, node.y + satDist*Math.sin(node.angle))),
    };
  }

  // Info panels below SVG
  const borrowed = hasSel ? getBorrowedChords(sel, isMinor) : [];
  const pivots   = hasSel ? findPivots(chords[selIdx].root, chords[selIdx].type, sel, mode) : [];

  return (
    <div ref={containerRef} style={{ width:"100%" }}>

      {/* Hint */}
      <div style={{ fontSize:10, color:"#bbb", fontFamily:"Inter,sans-serif", marginBottom:8, fontStyle:"italic" }}>
        Akkord antippen für Sekundärdominante, entlehnte Akkorde und Modulationsmöglichkeiten
      </div>

      {/* ── SVG graph ── */}
      <div style={{ borderRadius:12, border:"1px solid #ebebeb", background:"#fafafa", overflow:"hidden" }}>
        <svg width={W} height={H} style={{ display:"block" }}>
          <defs>
            <marker id="cn-s" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <polygon points="0,0 8,3 0,6" fill="#888" />
            </marker>
            <marker id="cn-m" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
              <polygon points="0,0 7,2.5 0,5" fill="#bbb" />
            </marker>
            <marker id="cn-f" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
              <polygon points="0,0 6,2 0,4" fill="#e8e8e8" />
            </marker>
            <marker id="cn-o" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
              <polygon points="0,0 7,2.5 0,5" fill="#f57c00" />
            </marker>
          </defs>

          {/* Diatonic connection arrows */}
          {connections.map(([from,to,strength],i) => {
            const f = nodes[from], t = nodes[to];
            const faded = hasSel && selIdx!==from && selIdx!==to;
            const lit   = hasSel && selIdx===from;
            const flip  = from > to; // separate bidirectional pairs
            const d     = arrowPath(f, t, CX, CY, flip);

            let stroke, sw, dash, mid;
            if (faded) {
              stroke="#e8e8e8"; sw=1; dash=undefined; mid="cn-f";
            } else if (strength==="strong") {
              stroke=lit?"#555":"#999"; sw=lit?3:2; dash=undefined; mid="cn-s";
            } else if (strength==="deceptive") {
              stroke=lit?"#666":"#bbb"; sw=1.5; dash="4,3"; mid="cn-m";
            } else {
              stroke=lit?"#777":"#ccc"; sw=1.5;
              dash=strength==="weak"?"5,4":undefined; mid="cn-m";
            }
            return (
              <path key={i} d={d} fill="none"
                stroke={stroke} strokeWidth={sw}
                strokeDasharray={dash}
                markerEnd={`url(#${mid})`}
              />
            );
          })}

          {/* Satellite dashed line */}
          {satellite && (
            <line
              x1={nodes[selIdx].x} y1={nodes[selIdx].y}
              x2={satellite.x}     y2={satellite.y}
              stroke="#f57c00" strokeWidth={1.5} strokeDasharray="4,3"
              opacity={0.65} markerEnd="url(#cn-o)"
            />
          )}

          {/* Diatonic chord nodes */}
          {nodes.map((node,i) => {
            const fc    = FC[node.fn] || FC.Tonika;
            const isSel = selIdx === i;
            const faded = hasSel && !isSel;
            return (
              <g key={i} onClick={() => setSelIdx(selIdx===i ? null : i)} style={{cursor:"pointer"}}>
                <circle cx={node.x} cy={node.y} r={NR}
                  fill={isSel ? fc.bd : faded ? "#f0f0f0" : fc.bg}
                  stroke={isSel ? fc.tx : faded ? "#ddd" : fc.bd}
                  strokeWidth={isSel ? 2.5 : 1.5}
                />
                <text x={node.x} y={node.y-4} textAnchor="middle" dominantBaseline="middle"
                  fontSize={node.label.length >= 4 ? 9 : 11} fontWeight="700"
                  fill={isSel?"#fff":faded?"#ccc":fc.tx}
                  fontFamily="Inter,sans-serif"
                  style={{pointerEvents:"none",userSelect:"none"}}>
                  {node.label}
                </text>
                <text x={node.x} y={node.y+9} textAnchor="middle" dominantBaseline="middle"
                  fontSize={7} fill={isSel?"rgba(255,255,255,0.7)":faded?"#ddd":fc.bd}
                  fontFamily="Inter,sans-serif"
                  style={{pointerEvents:"none",userSelect:"none"}}>
                  {node.degree}
                </text>
              </g>
            );
          })}

          {/* Secondary dominant satellite node */}
          {satellite && (
            <g>
              <circle cx={satellite.x} cy={satellite.y} r={22}
                fill="#fff3e0" stroke="#f57c00" strokeWidth={1.5} strokeDasharray="4,3" />
              <text x={satellite.x} y={satellite.y-3} textAnchor="middle" dominantBaseline="middle"
                fontSize={satellite.label.length>=5?8:9} fontWeight="700" fill="#e65100"
                fontFamily="Inter,sans-serif" style={{pointerEvents:"none",userSelect:"none"}}>
                {satellite.label}
              </text>
              <text x={satellite.x} y={satellite.y+8} textAnchor="middle" dominantBaseline="middle"
                fontSize={6} fill="#f57c00"
                fontFamily="Inter,sans-serif" style={{pointerEvents:"none",userSelect:"none"}}>
                {satellite.degree}
              </text>
            </g>
          )}

          {/* Legend (only when nothing selected) */}
          {!hasSel && (
            <g opacity={0.75}>
              <line x1={12} y1={H-28} x2={36} y2={H-28} stroke="#888" strokeWidth={2} markerEnd="url(#cn-s)"/>
              <text x={40} y={H-24} fontSize={8} fill="#aaa" fontFamily="Inter,sans-serif">stark</text>
              <line x1={80} y1={H-28} x2={104} y2={H-28} stroke="#bbb" strokeWidth={1.5} markerEnd="url(#cn-m)"/>
              <text x={108} y={H-24} fontSize={8} fill="#aaa" fontFamily="Inter,sans-serif">mittel</text>
              <line x1={150} y1={H-28} x2={174} y2={H-28} stroke="#bbb" strokeWidth={1.5} strokeDasharray="4,3" markerEnd="url(#cn-m)"/>
              <text x={178} y={H-24} fontSize={8} fill="#aaa" fontFamily="Inter,sans-serif">Trugschluss</text>
            </g>
          )}

          {/* Tap hint when selected */}
          {hasSel && (
            <text x={CX} y={H-10} textAnchor="middle" fontSize={9} fill="#ccc" fontFamily="Inter,sans-serif">
              nochmal tippen zum Abwählen
            </text>
          )}
        </svg>
      </div>

      {/* ── Info panels ── */}
      {hasSel && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:8 }}>

          {/* Secondary dominant */}
          {satellite && (
            <div style={{ padding:"10px 12px", borderRadius:10, background:"#fff3e0", border:"1px solid #ffcc80" }}>
              <div style={{ fontSize:9, color:"#e65100", fontWeight:700, letterSpacing:"0.06em", marginBottom:4 }}>
                SEKUNDÄRDOMINANTE
              </div>
              <div style={{ fontSize:13, fontWeight:700, color:"#e65100", fontFamily:"Inter,sans-serif", marginBottom:3 }}>
                {satellite.label} → {chords[selIdx].label}
              </div>
              <div style={{ fontSize:10, color:"#bf360c", fontFamily:"Inter,sans-serif", lineHeight:1.5 }}>
                Der Dominantakkord von {chords[selIdx].label}. Führt mit einem Leitton direkt in diesen Akkord — auch wenn er nicht zur Tonart gehört.
              </div>
            </div>
          )}

          {/* Borrowed chords */}
          {borrowed.length > 0 && (
            <div style={{ padding:"10px 12px", borderRadius:10, background:"#f3e5f5", border:"1px solid #ce93d8" }}>
              <div style={{ fontSize:9, color:"#6a1b9a", fontWeight:700, letterSpacing:"0.06em", marginBottom:6 }}>
                ENTLEHNTE AKKORDE — aus der {isMinor?"Dur":"Moll"}-Parallele
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
                {borrowed.map((b,i) => (
                  <div key={i} style={{
                    padding:"5px 10px", borderRadius:8,
                    background:"#fff", border:"1px solid #ce93d8",
                    fontFamily:"Inter,sans-serif",
                  }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#6a1b9a" }}>{b.label}</div>
                    <div style={{ fontSize:8, color:"#ab47bc", marginTop:1 }}>{b.degree}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:10, color:"#7b1fa2", fontFamily:"Inter,sans-serif", lineHeight:1.5, fontStyle:"italic" }}>
                Diese Akkorde stammen aus {root}-{isMinor?"Dur":"Moll"} und lassen sich direkt in die aktuelle Tonart einweben.
              </div>
            </div>
          )}

          {/* Pivot modulations */}
          {pivots.length > 0 && (
            <div style={{ padding:"10px 12px", borderRadius:10, background:"#e8f5e9", border:"1px solid #a5d6a7" }}>
              <div style={{ fontSize:9, color:"#2e7d32", fontWeight:700, letterSpacing:"0.06em", marginBottom:6 }}>
                {chords[selIdx].label.toUpperCase()} ALS PIVOT-AKKORD — MODULATION MÖGLICH
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
                {pivots.map((p,i) => (
                  <button key={i} onClick={() => onModulate?.(p.sel, p.mode)} style={{
                    padding:"6px 11px", borderRadius:8,
                    border:"1px solid #81c784", background:"#fff",
                    cursor:"pointer", fontFamily:"Inter,sans-serif",
                    display:"flex", flexDirection:"column", alignItems:"center", gap:1,
                    transition:"background 0.12s",
                  }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"#2e7d32" }}>{p.keyLabel}</span>
                    <span style={{ fontSize:8, color:"#66bb6a" }}>{p.degree} · {p.fn}</span>
                  </button>
                ))}
              </div>
              <div style={{ fontSize:9, color:"#388e3c", fontFamily:"Inter,sans-serif", fontStyle:"italic" }}>
                Tippen um direkt in diese Tonart zu wechseln →
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
